import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { FiGithub, FiExternalLink } from 'react-icons/fi'

const ProjectCard = ({ type, title, subtitle, description, image, projectLink, githubLink, tags }) => {
  return (
    <StyledProjectCard
      className="project-card"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.1 }}
      whileHover={{ y: -5 }}
    >
      <div className="card-image">
        <img src={image} alt={title} />
      </div>
      
      <div className="card-content">
        <div className="card-header">
          <span className="project-type">{type || (tags && tags[0])}</span>
          <h3>{title}</h3>
          {description && <p className="project-summary">{description}</p>}
        </div>
        
        <div className="card-footer">
          <div className="card-links">
            {githubLink && (
              <a href={githubLink} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                <FiGithub />
              </a>
            )}
            {projectLink && type !== "Mobile Application" && (
              <a href={projectLink} target="_blank" rel="noopener noreferrer" aria-label="Live Demo">
                <FiExternalLink />
              </a>
            )}
          </div>
        </div>
      </div>
    </StyledProjectCard>
  );
};

const FeaturedProject = ({ title, subtitle, description, image, projectLink, githubLink }) => {
  return (
    <StyledFeaturedProject
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >      <div className="project-content">
        <span className="project-label">FEATURED PROJECT</span>
        <h3>{title}</h3>
        {subtitle && <div className="project-subtitle">{subtitle}</div>}
        <div className="project-description">
          <p>{description}</p>
        </div>
        <div className="project-links">
          {githubLink && (
            <a href={githubLink} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <FiGithub />
            </a>
          )}
          {projectLink && (
            <a href={projectLink} target="_blank" rel="noopener noreferrer" aria-label="Live Demo">
              <FiExternalLink />
            </a>
          )}
        </div>
      </div>
      <div className="project-image">
        <a href={projectLink} target="_blank" rel="noopener noreferrer">
          <img src={image} alt={title} />
        </a>
      </div>
    </StyledFeaturedProject>
  );
};

const StyledFeaturedProject = styled(motion.div)`
  display: flex;
  position: relative;
  align-items: center;
  gap: 3rem;
  margin-top: 2rem;
  
  .project-content {
    flex: 1;
    z-index: 2;
    
    .project-label {
      color: var(--accent-color, #61DAFB);
      font-size: 0.8rem;
      font-weight: 600;
      margin-bottom: 0.75rem;
      letter-spacing: 0.15em;
    }
      h3 {
      font-size: 1.8rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
      color: var(--heading-text);
      letter-spacing: 0.03em;
    }
    
    .project-subtitle {
      font-size: 1rem;
      color: var(--accent-color, #61DAFB);
      margin-bottom: 1.5rem;
      font-weight: 500;
      letter-spacing: 0.02em;
    }
    
    .project-description {
      background: transparent;
      padding: 0;
      margin-bottom: 1.5rem;
      
      p {
        color: var(--secondary-text);
        font-size: 1rem;
        line-height: 1.8;
        letter-spacing: 0.05em;
      }
    }
    
    .project-links {
      display: flex;
      gap: 1.5rem;
      
      a {
        color: var(--primary-text);
        font-size: 1.3rem;
        transition: all 0.3s ease;
        
        &:hover {
          color: var(--accent-color, #61DAFB);
          transform: translateY(-3px);
        }
      }
    }
  }
  
  .project-image {
    flex: 1.2;
    position: relative;
    z-index: 1;
    
    a {
      display: block;
      border-radius: 8px;
      overflow: hidden;
      transition: all 0.3s ease;
      border: 1px solid rgba(var(--border-color-rgb), 0.1);
      
      &:hover {
        transform: translateY(-7px);
      }
    }
    
    img {
      width: 100%;
      height: auto;
      vertical-align: middle;
      transition: all 0.3s ease;
      filter: grayscale(20%);
      
      &:hover {
        filter: none;
      }
    }
  }
  
  @media (max-width: 992px) {
    flex-direction: column;
    gap: 2rem;
    
    .project-content, .project-image {
      width: 100%;
    }
  }
`;

const StyledProjectCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  backdrop-filter: blur(8px);
  z-index: 1;
  max-height: 380px;
    &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 12px;
    padding: 2px; /* Increased from 1px to 2px for better visibility */
    background: linear-gradient(
      120deg,
      rgba(var(--border-color-rgb), 0.2) 0%,
      rgba(var(--border-color-rgb), 0.1) 25%,
      rgba(var(--border-color-rgb), 0.3) 100%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    transition: all 0.6s ease;
    z-index: -1;
    pointer-events: none;
  }
  
  &:hover {
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.16);
    
    &::before {
      padding: 2px;
      background: linear-gradient(
        120deg,
        var(--vibrant, #4a5bef) 0%,
        var(--accent-color, #61DAFB) 50%,
        #8250DF 75%,
        rgba(var(--vibrant-rgb), 0.8) 100%
      );
      background-size: 300% 300%;
      transition: background-position 2s ease-in-out;
      background-position: 100% 100%;
    }
    
    .card-image img {
      transform: scale(1.05);
      filter: none;
    }
    
    h3 {
      color: var(--accent-color, #61DAFB);
    }
  }
  
  .card-image {
    position: relative;
    overflow: hidden;
    height: 180px;
    width: calc(100% - 8px);
    margin: 4px 4px 0;
    border-radius: 8px 8px 0 0;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s ease;
      filter: grayscale(20%);
    }
  }
  
  .card-content {
    padding: 1rem 1.2rem;
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: space-between;
    margin: 0 4px 4px;
    background: rgba(255, 255, 255, 0.01);
    border-radius: 0 0 8px 8px;
    
    .card-header {
      .project-type {
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        font-weight: 600;
        color: var(--accent-color, #61DAFB);
        margin-bottom: 0.5rem;
        display: inline-block;
        opacity: 0.9;
      }
      
      h3 {
        font-size: 1.1rem;
        font-weight: 600;
        margin-bottom: 0.6rem;
        color: var(--heading-text);
        letter-spacing: 0.02em;
        transition: color 0.3s ease;
      }
      
      .project-summary {
        color: var(--secondary-text);
        font-size: 0.85rem;
        line-height: 1.5;
        margin-bottom: 0.8rem;
        opacity: 0.9;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    
    .card-footer {
      margin-top: auto;
      padding-top: 0.7rem;
      border-top: 1px solid rgba(var(--border-color-rgb), 0.08);
      
      .card-links {
        display: flex;
        gap: 1rem;
        
        a {
          color: var(--primary-text);
          font-size: 1.05rem;
          transition: all 0.3s ease;
          opacity: 0.75;
          
          &:hover {
            color: var(--accent-color, #61DAFB);
            transform: translateY(-2px);
            opacity: 1;
          }
        }
      }
    }
  }
  
  @media (max-width: 768px) {
    .card-content {
      padding: 1.2rem;
      
      h3 {
        font-size: 1rem;
      }
      
      .project-summary {
        font-size: 0.8rem;
      }
    }
  }
`;

export { ProjectCard, FeaturedProject };